import clsx from 'clsx';
import { WorkspaceWindowPanelType } from '../../components/multi-window/types';
import { useAppDispatch, useAppSelector } from '../../hooks/store-hooks';
import { useDelayedUnmount } from '../../hooks/use-delayed-unmount';
import { setWorkspacePanelVisibility } from '../../state/slices/workspace-slice';
import './HomeSideBar.css';
import { AirportMonitor } from './panels/airport/AirportMonitor';
import { EVENT_LIST_COMPACT_WIDTH, EVENT_LIST_EXPANDED_WIDTH, EventList } from './panels/events/EventList';
import FleetMonitor from './panels/fleet/FleetMonitor';
import { HomePanel } from './panels/HomePanel';
import { MapLegend } from './panels/legend/MapLegend';

export function HomeSideBar() {
    const showFleetMonitor = useAppSelector((x) => x.workspace.panels.fleetMonitor);
    const showAirportMonitor = useAppSelector((x) => x.workspace.panels.airportMonitor);
    const showEventList = useAppSelector((x) => x.workspace.panels.eventList);
    const showLegend = useAppSelector((x) => x.workspace.panels.legend);
    const eventListExpanded = useAppSelector((x) => x.settings.eventListExpandedView);
    const dispatch = useAppDispatch();
    const routeAnalyzer = useAppSelector((x) => x.workspace.panels.routeAnalyzer);
    const canOpenRouteAnalyzer = !useAppSelector((x) =>
        x.workspace.windows.some((x) => x.panels.some((x) => x.type === WorkspaceWindowPanelType.routeAnalyzer))
    );

    const shouldRenderFleetMonitor = useDelayedUnmount(showFleetMonitor);
    const shouldRenderAirportMonitor = useDelayedUnmount(showAirportMonitor);
    const shouldRenderEventList = useDelayedUnmount(showEventList);
    const shouldRenderLegend = useDelayedUnmount(showLegend);

    return (
        <div className="d-flex" id="monitor-panels">
            <div className="d-flex">
                <div className={`sidebar-slide ${showLegend ? 'sidebar-slide-visible' : ''}`}>
                    <div className="border-left" style={{ width: 150 }}>
                        {shouldRenderLegend && (
                            <HomePanel title="Map legend" className="h-100">
                                <MapLegend />
                            </HomePanel>
                        )}
                    </div>
                </div>
                <div className={`sidebar-slide ${showEventList ? 'sidebar-slide-visible' : ''}`}>
                    <div
                        className="border-left"
                        style={{ width: eventListExpanded ? EVENT_LIST_EXPANDED_WIDTH : EVENT_LIST_COMPACT_WIDTH }}
                        id="event-list">
                        {shouldRenderEventList && (
                            <HomePanel title="Event list" className="h-100">
                                <EventList />
                            </HomePanel>
                        )}
                    </div>
                </div>

                <div className={`sidebar-slide ${showAirportMonitor ? 'sidebar-slide-visible' : ''}`}>
                    <div className="border-left" style={{ width: 170 }} id="airport-monitor">
                        {shouldRenderAirportMonitor && (
                            <HomePanel title="Airport monitor" className="h-100">
                                <AirportMonitor />
                            </HomePanel>
                        )}
                    </div>
                </div>

                <div className={`sidebar-slide ${showFleetMonitor ? 'sidebar-slide-visible' : ''}`}>
                    <div className="border-left" style={{ width: 250 }} id="fleet-monitor">
                        {shouldRenderFleetMonitor && (
                            <HomePanel title="Fleet monitor" className="h-100">
                                <FleetMonitor />
                            </HomePanel>
                        )}
                    </div>
                </div>
            </div>

            <div className="border-left d-flex flex-column gap-2" style={{ fontSize: '0.9rem' }}>
                <SideBarItem
                    id="home-fleet-monitor"
                    title="Fleet Monitor"
                    active={showFleetMonitor}
                    onClick={() => dispatch(setWorkspacePanelVisibility({ panel: 'fleetMonitor', visible: !showFleetMonitor }))}
                />
                <SideBarItem
                    id="home-airport-monitor"
                    title="Airport Monitor"
                    active={showAirportMonitor}
                    onClick={() => dispatch(setWorkspacePanelVisibility({ panel: 'airportMonitor', visible: !showAirportMonitor }))}
                />
                <SideBarItem
                    title="Event list"
                    id="home-event-list"
                    active={showEventList}
                    onClick={() => dispatch(setWorkspacePanelVisibility({ panel: 'eventList', visible: !showEventList }))}
                />
                <SideBarItem
                    title="Legend"
                    active={showLegend}
                    onClick={() => dispatch(setWorkspacePanelVisibility({ panel: 'legend', visible: !showLegend }))}
                />
                <div className="flex-grow-1"></div>
                <SideBarItem
                    id="home-route-analyzer"
                    title="Route analyzer"
                    active={routeAnalyzer}
                    disabled={!canOpenRouteAnalyzer}
                    onClick={() => dispatch(setWorkspacePanelVisibility({ panel: 'routeAnalyzer', visible: !routeAnalyzer }))}
                />
            </div>
        </div>
    );
}

function SideBarItem(props: { id?: string; title: string; active: boolean; onClick?: () => void; disabled?: boolean }) {
    return (
        <div
            id={props.id}
            className={clsx(props.disabled ? 'opacity-50' : 'clickable', props.active && 'active')}
            style={{ writingMode: 'vertical-lr', userSelect: 'none' }}
            onClick={props.disabled ? undefined : props.onClick}>
            <div className="py-2">{props.title}</div>
            <div style={{ width: 3, backgroundColor: props.active ? 'var(--nav-selected-bg-color)' : 'var(--nav-bg-color)' }}></div>
        </div>
    );
}
